import React from "react";
import { useSelector } from "react-redux";
import { withMomentLocale } from "@/utils/eventFormat";
import AppLogo from "@/components/AppLogo";
import StudentAvatar from "./StudentAvatar";

export interface StudentNotification {
  details?: {
    id?: string;
    title?: string | null;
    title_en?: string | null;
    title_zh_hant?: string | null;
    message?: string | null;
    is_read?: boolean;
    ctime?: string | null;
    entity_type?: string | null;
    entity_id?: string | null;
    sender_info?: {
      profile_icon?: string | null;
      role?: string | null;
    } | null;
  } | null;
}

interface StudentNotificationRowProps {
  notification: StudentNotification;
  onPress?: () => void;
}

/**
 * One notification card — the web port of the RN `Notifications` row: sender
 * avatar (the app icon for admin senders), localized title, relative time and
 * an accent unread state.
 */
const StudentNotificationRow: React.FC<StudentNotificationRowProps> = ({
  notification,
  onPress,
}) => {
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  const m = withMomentLocale(language);

  const details = notification?.details ?? {};
  const isRead = Boolean(details.is_read);
  const senderRole = details.sender_info?.role;
  const title =
    (language === "zh_hant" ? details.title_zh_hant : details.title_en) ||
    details.title_en ||
    details.title_zh_hant ||
    details.title ||
    "";
  const relativeTime = details.ctime ? m(details.ctime).fromNow() : "";

  return (
    <button
      type="button"
      onClick={onPress}
      className={`flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left shadow-sm transition ${
        isRead
          ? "border-gray-100 bg-white"
          : "border-[#ffe0b2] bg-[#fff8f0] hover:bg-[#fff3e0]"
      }`}
    >
      <span className="relative shrink-0">
        {senderRole === "admin" ? (
          <AppLogo size={48} className="h-12 w-12 rounded-2xl" />
        ) : (
          <StudentAvatar
            src={details.sender_info?.profile_icon}
            name={title}
            size="md"
          />
        )}
        {!isRead && (
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-[#ffa217]" />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-sm ${
            isRead ? "font-semibold text-gray-900" : "font-bold text-gray-900"
          }`}
        >
          {title}
        </span>
        {details.message && (
          <span className="mt-0.5 line-clamp-2 block text-xs text-gray-500">
            {details.message}
          </span>
        )}
        {relativeTime && (
          <span className="mt-1 block text-xs text-gray-400">
            {relativeTime}
          </span>
        )}
      </span>
    </button>
  );
};

export default StudentNotificationRow;
